import { REVENUE_BASES, REVENUE_BASIS_LABELS, type RevenueBasis } from '@/api/revenue'
import { SectionFrame } from '@/app/pages'
import type { RouteDef } from '@/app/routes'
import { SegmentedControl, type FormatOptions } from '@/design'
import { useMoney } from '@/features/overview/hooks'
import type { GlobalFilters } from '@/lib/filters'
import { usePageFreshness } from '@/lib/freshness'
import { useGlobalFilters } from '@/lib/useGlobalFilters'

import { BreakdownTab } from './breakdown/BreakdownTab'
import { comparisonLabel } from './format'
import { useBasis, usePeriod, useRevenueTab, type RevenuePeriod, type RevenueTab } from './params'
import { useRevenueSummary } from './queries'
import { RevenueTabPanel, RevenueTabs } from './RevenueTabs'
import { LeakageTabSlot, OrdersTabSlot } from './slots'
import type { SummaryRead } from './summary/BreakdownPanels'
import { SummaryTab } from './summary/SummaryTab'
import { TimingTab } from './timing/TimingTab'
import './revenue.css'

/** The page-wide basis switch: every panel reads under the one chosen, so gross and net never sit side by side. */
export function BasisToggle({ value, onChange }: { value: RevenueBasis; onChange: (next: RevenueBasis) => void }) {
  return (
    <SegmentedControl
      label="Revenue basis"
      options={REVENUE_BASES.map((basis) => ({ value: basis, label: REVENUE_BASIS_LABELS[basis] }))}
      value={value}
      onChange={onChange}
    />
  )
}

/**
 * Analytics › Revenue: where the revenue came from, when, and how it compares, on the
 * revenue read model (`modules/revenue`). The filter bar's period, website, environments
 * and comparison apply to every tab, and the page's own basis toggle to every figure. Its
 * freshness is the summary's newest source row, declared once whatever the tab; the
 * Leakage and Orders tabs and the lifecycle panel are the follow-on card's slots.
 */
export function RevenuePage({ route }: { route: RouteDef }) {
  const { filters } = useGlobalFilters()
  const [basis, setBasis] = useBasis()
  const [tab, setTab] = useRevenueTab()
  const summary = useRevenueSummary(filters, basis)
  const period = usePeriod(filters)
  usePageFreshness(summary.data?.meta)
  const money = useMoney(summary.data?.meta.currency)
  const frame = summary.data?.data
  const label = summary.data?.meta.period?.label

  return (
    <SectionFrame
      route={route}
      description={label ? `${label}${frame ? ` · ${comparisonLabel(frame)}` : ''}` : undefined}
      actions={<BasisToggle value={basis} onChange={setBasis} />}
    >
      <div className="rv-page" data-basis={basis}>
        <RevenueTabs active={tab} onSelect={setTab} />
        <RevenueTabPanel tab={tab}>
          <Panel tab={tab} filters={filters} basis={basis} period={period} summary={summary} money={money} />
        </RevenueTabPanel>
      </div>
    </SectionFrame>
  )
}

/** The open tab's content. Only this one is mounted, so a tab's routes are read only while it is open. */
function Panel({
  tab,
  filters,
  basis,
  period,
  summary,
  money,
}: {
  tab: RevenueTab
  filters: GlobalFilters
  basis: RevenueBasis
  period: RevenuePeriod
  summary: SummaryRead
  money: FormatOptions
}) {
  switch (tab) {
    case 'summary':
      return <SummaryTab filters={filters} basis={basis} period={period} summary={summary} money={money} />
    case 'competition':
    case 'channel':
    case 'customer':
      return <BreakdownTab key={tab} tab={tab} filters={filters} basis={basis} money={money} />
    case 'timing':
      return <TimingTab filters={filters} basis={basis} money={money} />
    case 'leakage':
      return <LeakageTabSlot filters={filters} basis={basis} money={money} />
    case 'orders':
      return <OrdersTabSlot filters={filters} basis={basis} money={money} />
  }
}
